import { gql, useApolloClient, useQuery } from '@apollo/client';
import Icone from 'components/Icone';
import Modal from 'components/modal/base';
import { format, parseISO } from 'date-fns';
import { GRAPHQL_SAQUE_ITENS } from 'gql/saque';
import React, { FC } from 'react';
import { useParams } from 'react-router-dom';
import { IQuerySaqueItens, ISaque, ISaqueItem } from '../../list/hooks/index.d';
import { Container, Header, Lista, Mensagem, Totais } from './styl';

const FRAGMENT_SAQUE = gql`
    fragment SaqueDetalhes on Saque {
        entregadorNome
        valorSolicitado
        taxaAntecipacao
        valorLiquido
    }
`;

const moeda = (valor?: number) =>
    new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    }).format(valor || 0);

const data = (item: ISaqueItem) =>
    item.dataHora
        ? format(parseISO(item.dataHora), 'dd/MM/yyyy HH:mm')
        : format(parseISO(item.data), 'dd/MM/yyyy');

const soma = (itens: ISaqueItem[]) =>
    itens.reduce((total, item) => total + (item.valor || 0), 0);

const PageSaqueDetalhes: FC = () => {
    const { id } = useParams<{ id: string }>();
    const client = useApolloClient();
    // Já carregado pela listagem; ausente quando o modal é aberto direto pela URL
    const saque = client.readFragment<Partial<ISaque>>({
        id: `Saque:${id}`,
        fragment: FRAGMENT_SAQUE,
    });
    const { data: resposta, loading, error } = useQuery<IQuerySaqueItens>(
        GRAPHQL_SAQUE_ITENS,
        { variables: { id }, fetchPolicy: 'network-only' }
    );
    const itens = resposta?.saqueItens || [];
    const corridas = itens.filter((item) => item.tipo === 'CORRIDA');
    const garantidos = itens.filter((item) => item.tipo === 'GARANTIDO');

    return (
        <Modal
            render={({ closeModal }) => (
                <Container>
                    <Header>
                        <div>
                            <h1>Corridas do saque #{id}</h1>
                            {saque?.entregadorNome && (
                                <span>{saque.entregadorNome}</span>
                            )}
                        </div>
                        <button onClick={closeModal}>
                            <Icone
                                icone="icon-fechar"
                                size="2rem"
                                color="var(--btn-admin-icone)"
                            />
                        </button>
                    </Header>
                    {loading && <Mensagem>Carregando...</Mensagem>}
                    {error && <Mensagem>{error.message}</Mensagem>}
                    {!loading && !error && itens.length === 0 && (
                        <Mensagem>Nenhuma corrida encontrada.</Mensagem>
                    )}
                    {itens.length > 0 && (
                        <>
                            <Lista>
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Corrida</th>
                                            <th>Unidade</th>
                                            <th>Data</th>
                                            <th>Turno</th>
                                            <th>Valor</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {itens.map((item) => (
                                            <tr key={item.chave}>
                                                <td>
                                                    {item.tipo === 'CORRIDA'
                                                        ? item.numeroInterno
                                                        : 'Complemento do garantido'}
                                                </td>
                                                <td>
                                                    {item.unidadeNomeFantasia ||
                                                        '-'}
                                                </td>
                                                <td>{data(item)}</td>
                                                <td>
                                                    {item.periodoNome}
                                                    <small>
                                                        {item.periodoHoraInicio}{' '}
                                                        às {item.periodoHoraFim}
                                                    </small>
                                                </td>
                                                <td>{moeda(item.valor)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </Lista>
                            <Totais>
                                <div>
                                    {corridas.length} corrida(s)
                                    <strong>{moeda(soma(corridas))}</strong>
                                </div>
                                {garantidos.length > 0 && (
                                    <div>
                                        Garantido
                                        <strong>
                                            {moeda(soma(garantidos))}
                                        </strong>
                                    </div>
                                )}
                                {saque && (
                                    <>
                                        <div>
                                            Taxa
                                            <strong>
                                                {moeda(saque.taxaAntecipacao)}
                                            </strong>
                                        </div>
                                        <div>
                                            Valor líquido
                                            <strong>
                                                {moeda(saque.valorLiquido)}
                                            </strong>
                                        </div>
                                    </>
                                )}
                            </Totais>
                        </>
                    )}
                </Container>
            )}
        />
    );
};

export default PageSaqueDetalhes;
